
import React from 'react';
import { Evidence } from '../../services/api';
import { formatAddress } from '../../services/web3';

interface DisputeDetailSectionProps {
  disputeStatus: string;
  disputeReason?: string;
  disputeInitiator?: string;
  evidence: Evidence[];
  canSubmitEvidence: boolean;
  onSubmitEvidence: () => void;
  clientAddress?: string;
  providerAddress?: string;
  arbitratorAddress?: string | null;
}

const DisputeDetailSection: React.FC<DisputeDetailSectionProps> = ({
  disputeStatus,
  disputeReason,
  disputeInitiator,
  evidence,
  canSubmitEvidence,
  onSubmitEvidence,
  clientAddress,
  providerAddress,
  arbitratorAddress,
}) => {
  const getRoleName = (address: string) => {
    const lower = address.toLowerCase();
    if (clientAddress && lower === clientAddress.toLowerCase()) return 'Client';
    if (providerAddress && lower === providerAddress.toLowerCase()) return 'Provider';
    if (arbitratorAddress && lower === arbitratorAddress.toLowerCase()) return 'Arbitrator';
    return formatAddress(address);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDocumentUrl = (blobId: string) => {
    const base = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001');
    return `${base}/api/get-evidence-document?blobId=${encodeURIComponent(blobId)}`;
  };

  return (
    <div className="mt-3 border-t border-[#471b001e] pt-3">
      {/* Dispute info */}
      {disputeReason && (
        <div className="mb-3">
          <p className="text-xs font-medium text-[#471b00] font-dm mb-1">Dispute Reason:</p>
          <p className="text-xs text-[#471b007f] font-dm bg-red-50 rounded-lg p-2">
            {disputeReason}
          </p>
          {disputeInitiator && (
            <p className="text-xs text-[#471b007f] font-dm mt-1">
              Opened by: <span className="font-medium">{getRoleName(disputeInitiator)}</span> ({formatAddress(disputeInitiator)})
            </p>
          )}
        </div>
      )}

      {/* Evidence timeline */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs font-medium text-[#471b00] font-dm">
            Evidence ({evidence.length})
          </p>
          {canSubmitEvidence && disputeStatus !== 'resolved' && (
            <button
              onClick={onSubmitEvidence}
              className="px-3 py-1 text-xs border border-[#FF3333] text-[#FF3333] rounded-lg font-medium hover:bg-red-50 transition-colors font-dm"
            >
              Submit Evidence
            </button>
          )}
        </div>

        {evidence.length === 0 ? (
          <p className="text-xs text-[#471b007f] font-dm text-center py-3 bg-gray-50 rounded-lg">
            No evidence submitted yet
          </p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {evidence.map((e) => (
              <div key={e.id} className="bg-gray-50 rounded-lg p-3 text-xs font-dm">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-[#471b00]">
                    {getRoleName(e.submittedBy)}
                  </span>
                  <span className="text-[#471b007f]">
                    {formatDateTime(e.submittedAt)}
                  </span>
                </div>

                {e.evidenceType === 'text' && e.evidenceText && (
                  <p className="text-[#471b00] whitespace-pre-wrap mt-1">{e.evidenceText}</p>
                )}

                {e.evidenceType === 'document' && e.blobId && (
                  <a
                    href={getDocumentUrl(e.blobId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#FF3333] hover:underline mt-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download document
                  </a>
                )}

                {e.submissionNotes && (
                  <p className="text-[#471b007f] italic mt-1">Note: {e.submissionNotes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeDetailSection;
